import { useState } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Users, ClipboardList, FileText, History, Loader2, FolderArchive, Bell } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminExports() {
  const [exporting, setExporting] = useState({});

  const exportOptions = [
    {
      id: 'drivers',
      title: 'Drivers List',
      description: 'Export all driver accounts with their details',
      icon: Users,
      endpoint: '/export/drivers',
      type: 'csv'
    },
    {
      id: 'registrations',
      title: 'Registrations',
      description: 'Export all driver registration applications',
      icon: ClipboardList,
      endpoint: '/export/registrations',
      type: 'csv'
    },
    {
      id: 'statements',
      title: 'Statements Log',
      description: 'Export statement upload history',
      icon: FileText,
      endpoint: '/export/statements',
      type: 'csv'
    },
    {
      id: 'audit-logs',
      title: 'Audit Logs',
      description: 'Export system audit logs',
      icon: History,
      endpoint: '/export/audit-logs',
      type: 'csv'
    }
  ];

  const bulkExportOptions = [
    {
      id: 'all-driver-documents',
      title: 'All Driver Documents',
      description: 'Download all statements, registration docs, and contracts for ALL drivers in one ZIP file. Folders named by driver.',
      icon: FolderArchive,
      endpoint: '/export/all-driver-documents',
      type: 'zip'
    }
  ];

  const handleExport = async (option) => {
    setExporting({ ...exporting, [option.id]: true });
    
    try {
      if (option.type === 'zip') {
        // Handle ZIP download
        const response = await axios.get(`${API_URL}${option.endpoint}`, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        
        // Get filename from response headers or generate one
        const contentDisposition = response.headers['content-disposition'];
        let filename = `export_${new Date().toISOString().split('T')[0]}.zip`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success(`${option.title} exported successfully`);
      } else {
        // Handle CSV export
        const response = await axios.get(`${API_URL}${option.endpoint}`);
        const data = response.data.data;
        
        if (!data || data.length === 0) {
          toast.info('No data to export');
          return;
        }
        
        // Convert to CSV
        const headers = Object.keys(data[0]);
        const csvContent = [
          headers.join(','),
          ...data.map(row => 
            headers.map(header => {
              let value = row[header];
              if (typeof value === 'object') {
                value = JSON.stringify(value);
              }
              // Escape commas and quotes
              if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                value = `"${value.replace(/"/g, '""')}"`;
              }
              return value ?? '';
            }).join(',')
          )
        ].join('\n');
        
        // Download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${option.id}_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success(`${option.title} exported successfully`);
      }
    } catch (error) {
      toast.error(`Failed to export ${option.title.toLowerCase()}`);
    } finally {
      setExporting({ ...exporting, [option.id]: false });
    }
  };

  const handleSendExpiryAlerts = async () => {
    setExporting({ ...exporting, 'expiry-alerts': true });
    try {
      await axios.post(`${API_URL}/notifications/send-expiry-alerts`);
      toast.success('Document expiry alerts sent to drivers');
    } catch (error) {
      toast.error('Failed to send expiry alerts');
    } finally {
      setExporting({ ...exporting, 'expiry-alerts': false });
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-exports">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Export Data</h1>
          <p className="text-muted-foreground mt-1">Download data and documents</p>
        </div>

        {/* CSV Exports */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Data Exports (CSV)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {exportOptions.map((option) => {
              const Icon = option.icon;
              const isExporting = exporting[option.id];
              
              return (
                <Card key={option.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
                          <Icon className="h-6 w-6 text-primary-foreground" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{option.title}</CardTitle>
                          <CardDescription className="mt-1">{option.description}</CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => handleExport(option)}
                      disabled={isExporting}
                      className="w-full"
                      data-testid={`export-${option.id}-button`}
                    >
                      {isExporting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Export to CSV
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Bulk Document Export */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Bulk Document Export (ZIP)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {bulkExportOptions.map((option) => {
              const Icon = option.icon;
              const isExporting = exporting[option.id];
              
              return (
                <Card key={option.id} className="hover:shadow-md transition-shadow border-2 border-dashed border-primary/30">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-green-500 flex items-center justify-center">
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{option.title}</CardTitle>
                          <CardDescription className="mt-1">{option.description}</CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => handleExport(option)}
                      disabled={isExporting}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid={`export-${option.id}-button`}
                    >
                      {isExporting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Preparing ZIP...
                        </>
                      ) : (
                        <>
                          <FolderArchive className="h-4 w-4 mr-2" />
                          Download All Documents
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Notifications Section */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Notifications</h2>
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-amber-500 flex items-center justify-center">
                  <Bell className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg">Send Document Expiry Alerts</CardTitle>
                  <CardDescription className="mt-1">
                    Manually trigger expiry alerts for all drivers with documents expiring within 30 days
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleSendExpiryAlerts}
                disabled={exporting['expiry-alerts']}
                variant="outline"
                className="w-full md:w-auto border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="send-expiry-alerts-button"
              >
                {exporting['expiry-alerts'] ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Bell className="h-4 w-4 mr-2" />
                    Send Expiry Alerts
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Export Information</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>• CSV exports are compatible with Excel, Google Sheets, and other spreadsheet applications.</p>
            <p>• Bulk document export creates a ZIP file with folders for each driver, containing their statements, registration docs, and contracts.</p>
            <p>• Document expiry alerts are sent via Email and WhatsApp (when API keys are configured).</p>
            <p>• All export actions are logged in the audit trail for compliance purposes.</p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
